/**
 * API client for de-hub `spatial` module.
 *
 * Thin wrappers over the shared `dataService` — keeps spatial endpoint paths
 * in one place. All endpoints return standard FeatureCollections inside the
 * usual de-hub envelope so callers pull `.data.data` (or `.data`, depending
 * on the endpoint's shape once landed).
 *
 * Endpoints (see plan-of-record):
 *   GET /api/spatial/states
 *   GET /api/spatial/counties?state=OK
 *   GET /api/spatial/plss/sections?state=OK&county=GRADY[&bbox=minx,miny,maxx,maxy]
 *   GET /api/spatial/plss/options?state=OK[&county=GRADY][&twp=07N][&rng=07W]
 *   GET /api/spatial/occ/units?state=OK&county=GRADY&kinds=pooling,spacing
 *   GET /api/spatial/occ/intents?state=OK&county=GRADY
 *
 * `plss/options` is the odd one out — it returns TRS pick-list values
 * rather than geometry, so its payload is `{ townships, ranges, sections }`
 * and not a FeatureCollection.
 */
import dataService from "./dataservice";

const BASE = "/spatial";

const spatialService = {
  getStates: (token, userOid) =>
    dataService.request("get", `${BASE}/states`, token, userOid, undefined),

  getCounties: (token, userOid, state) =>
    dataService.request(
      "get",
      `${BASE}/counties`,
      token,
      userOid,
      undefined,
      { params: { state } }
    ),

  getSections: (token, userOid, { state, county, bbox } = {}) =>
    dataService.request(
      "get",
      `${BASE}/plss/sections`,
      token,
      userOid,
      undefined,
      {
        params: {
          state,
          county,
          ...(bbox ? { bbox: bbox.join(",") } : {}),
        },
      }
    ),

  /**
   * Township / Range / Section pick-list values for a venue.
   *
   * Each list is scoped by the levels above it — `townships` ignores
   * `twp`/`rng`, `ranges` honours `twp`, `sections` honours both — so one
   * call refills the whole cascade after a change at any level.
   *
   * Optional params are omitted rather than sent empty: the API treats a
   * blank `county` as "no filter" either way, but leaving them out keeps
   * the URL (and any response cache keyed off it) stable.
   *
   * @returns {Promise} envelope with `data: { state, county, twp, rng,
   *   townships: string[], ranges: string[], sections: string[] }`
   */
  getPlssOptions: (token, userOid, { state, county, twp, rng } = {}) =>
    dataService.request(
      "get",
      `${BASE}/plss/options`,
      token,
      userOid,
      undefined,
      {
        params: {
          state,
          ...(county ? { county } : {}),
          ...(twp ? { twp } : {}),
          ...(rng ? { rng } : {}),
        },
      }
    ),

  /**
   * OCC unit polygons (pooling / spacing / increased density / location
   * exception) for one county. Oklahoma only; `county` is required — the
   * caller must not ask without one.
   *
   * @param {{ state: string, county: string, kinds: string[] }} args
   * @returns {Promise} envelope with `data: FeatureCollection`; each feature's
   *   `properties.kind` is one of the requested kinds
   */
  getOccUnits: (token, userOid, { state, county, kinds } = {}) =>
    dataService.request(
      "get",
      `${BASE}/occ/units`,
      token,
      userOid,
      undefined,
      {
        params: {
          state,
          county,
          kinds: (Array.isArray(kinds) ? kinds : []).join(","),
        },
      }
    ),

  /**
   * OCC intents to drill (well permits) for one county, as Points.
   * Oklahoma only (other states return an empty collection); `county` is
   * required — the caller must not ask without one.
   *
   * @param {{ state: string, county: string }} args
   * @returns {Promise} envelope with `data: FeatureCollection` of Point
   *   features, one per permit
   */
  getOccIntents: (token, userOid, { state, county } = {}) =>
    dataService.request(
      "get",
      `${BASE}/occ/intents`,
      token,
      userOid,
      undefined,
      { params: { state, county } }
    ),
};

export default spatialService;
